import type { FAQ } from './types';

/**
 * Reseller-audience FAQs. Loaded ONLY for users whose JWT has
 * tenant_type='reseller' or 'platform' (the platform admin acts as a
 * super-reseller). Customers never see these because the admin
 * surface they describe (rate cards, plan management, sender pool)
 * is gated by RequireReseller on the backend.
 *
 * Same `id`/audience parity rule as customer.*: any new entry must
 * exist with the same id in BOTH reseller.es.tsx and reseller.en.tsx.
 */
export const RESELLER_FAQS_ES: FAQ[] = [
  // =============================================================================
  // Customer management
  // =============================================================================
  {
    id: 'customers-create',
    category: 'customers',
    question: '¿Cómo creo un nuevo customer?',
    tags: ['cliente', 'sub-tenant', 'crear'],
    cta: { label: 'Ir a Customers', to: '/admin/customers' },
    answer: (
      <p>
        Admin → <strong>Customers</strong> → "Crear". Le ponés nombre, email
        del owner, password inicial, país y zona horaria. Opcionalmente le
        acreditás un balance inicial. Recibe un email para entrar y se le
        pre-asigna tu rate card default.
      </p>
    ),
  },
  {
    id: 'customers-type-picker',
    category: 'customers',
    question: 'Customer vs Reseller — ¿cuál tipo elijo al crear?',
    tags: ['tipo', 'reseller', 'sub-reseller'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Customer</strong>: cliente final. No puede tener clientes propios.</li>
        <li><strong>Reseller</strong>: puede tener sus propios customers debajo. Útil para crear sub-resellers (white-label de white-label).</li>
        <li>Si te equivocaste, en el detail page hay un botón "Promote to reseller".</li>
      </ul>
    ),
  },
  {
    id: 'customers-promote',
    category: 'customers',
    question: '¿Cómo promuevo un customer a reseller?',
    tags: ['promote', 'upgrade'],
    answer: (
      <p>
        En el detail del customer (<code className="mono">/admin/customers/:id</code>)
        hay un botón "Promote to reseller". Solo platform admin lo ve.
        Cambia <code className="mono">type='reseller'</code> y desbloquea
        que ese tenant cree sus propios customers.
      </p>
    ),
  },
  {
    id: 'customers-suspend',
    category: 'customers',
    question: '¿Qué pasa cuando suspendo un customer?',
    tags: ['suspender', 'bloquear', 'pause'],
    answer: (
      <p>
        El status pasa a <code className="mono">suspended</code>. Sus envíos
        nuevos se rechazan (<code className="mono">tenant_suspended</code>),
        pero los datos quedan intactos. Los users del customer no pueden
        loguear. Reactivá con un click — todo vuelve sin pérdida.
      </p>
    ),
  },
  {
    id: 'customers-impersonate',
    category: 'customers',
    question: '¿Qué hace "View as customer" (impersonation)?',
    tags: ['impersonate', 'ver como', 'support'],
    answer: (
      <p>
        Te abre una nueva sesión actuando como el owner del customer. Útil
        para support — ves exactamente lo que ve el cliente. Un banner en
        el topbar deja claro que estás impersonando + qué admin lo lanzó.
        El audit log registra cada acción que hagas en ese modo.
      </p>
    ),
  },
  {
    id: 'customers-credit-limit',
    category: 'customers',
    question: '¿Cómo le doy crédito post-pago a un customer?',
    tags: ['credit', 'postpago', 'limit'],
    answer: (
      <p>
        En el detail → "Credit limit". Default es 0 (estricto pre-pago: balance
        debe ser &gt; 0 para enviar). Cualquier valor mayor convierte al
        customer en post-pago hasta ese monto. El dispatcher acepta envíos
        mientras (balance + credit_limit) ≥ costo.
      </p>
    ),
  },
  {
    id: 'customers-low-balance-threshold',
    category: 'customers',
    question: '¿Puedo cambiar el umbral de alerta de balance bajo?',
    tags: ['threshold', 'alerta'],
    answer: (
      <p>
        Sí. Detail del customer → "Low balance threshold". Default $10. Por
        debajo de ese número, el cron horario le manda un email + le pinta
        un banner en la app. Setealo en 0 para desactivar las alertas.
      </p>
    ),
  },

  // =============================================================================
  // Pricing (rate cards, plans, overrides)
  // =============================================================================
  {
    id: 'pricing-rate-cards-vs-plans',
    category: 'billing',
    question: 'Rate card vs Plan — ¿cuál es la diferencia?',
    tags: ['precio', 'tarifario'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Rate card</strong>: contenedor de precios por destino (prefix). Una card tiene N rates.</li>
        <li><strong>Plan</strong>: paquete de suscripción (fee mensual + SMS incluidos + overage) que apunta a un rate card.</li>
        <li>Cliente típico: el plan le dice cuánto paga al mes; el rate card le dice cuánto le cuesta cada SMS extra.</li>
      </ul>
    ),
  },
  {
    id: 'pricing-create-rate-card',
    category: 'billing',
    question: '¿Cómo creo un rate card?',
    tags: ['rate card', 'tarifario', 'crear'],
    cta: { label: 'Ir a Rate cards', to: '/admin/rate-cards' },
    answer: (
      <p>
        Admin → <strong>Rate cards</strong> → "Crear". Nombre + estrategia
        (LCR / LCD / priority). Después entrás a la card y agregás rates por
        prefijo (1 = US, 57 = Colombia, etc) con buy_cost + sell_price +
        carrier al que rutea.
      </p>
    ),
  },
  {
    id: 'pricing-strategy',
    category: 'billing',
    question: 'LCR vs LCD vs priority — ¿qué estrategia uso?',
    tags: ['strategy', 'routing', 'lcr'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>LCR</strong> (least cost routing): elige el rate más barato que matchea. Optimiza tu margen.</li>
        <li><strong>LCD</strong> (least cost delivery): elige el sell_price más bajo. Mejor precio para el cliente.</li>
        <li><strong>Priority</strong>: la priority del rate gana, ignora costos. Para SLAs / pin a un carrier específico.</li>
      </ul>
    ),
  },
  {
    id: 'pricing-cascade',
    category: 'billing',
    question: '¿Cómo decide el sistema qué rate aplicar a un envío?',
    tags: ['cascade', 'routing', 'cascada'],
    answer: (
      <p>
        El customer puede tener múltiples rate cards asignados (con priority
        cada uno). El dispatcher prueba en cascade: card más prioritaria
        primero, busca el rate que matchea destino + carrier sano + ventana
        válida. Primer match gana. Si nada matchea, el envío queda en
        <code className="mono"> no_route_available</code>.
      </p>
    ),
  },
  {
    id: 'pricing-assign-card',
    category: 'billing',
    question: '¿Cómo le asigno un rate card a un customer?',
    tags: ['assign', 'asignar'],
    answer: (
      <p>
        En el detail del customer hay una sección "Rate cards" con sus
        cards asignadas. "Asignar" abre un picker de tus cards. La priority
        define qué card se prueba primero en el cascade. Podés tener N
        cards asignadas por customer.
      </p>
    ),
  },
  {
    id: 'pricing-cutover',
    category: 'billing',
    question: '¿Cómo programo un cambio de precio para una fecha futura?',
    tags: ['cutover', 'effective_from', 'cambio'],
    answer: (
      <p>
        En el rate card, "Cutover" te deja setear una fecha de efectividad
        para un nuevo precio. El sistema crea un rate nuevo con
        <code className="mono"> effective_from = fecha</code> y ajusta el viejo
        para que termine en esa fecha. Validación de wholesale floor sigue
        aplicando.
      </p>
    ),
  },
  {
    id: 'pricing-plan-override',
    category: 'billing',
    question: '¿Qué es un plan override y cuándo lo uso?',
    tags: ['override', 'plan', 'descuento'],
    answer: (
      <p>
        Un override fuerza un sell_price específico para un destino dentro
        de un plan, ignorando el rate card. Útil para promos puntuales
        ("US a $0.005 hasta diciembre") sin tener que crear un rate card
        nuevo. El backend valida que no esté por debajo del wholesale.
      </p>
    ),
  },
  {
    id: 'pricing-create-plan',
    category: 'billing',
    question: '¿Cómo creo un plan que mis customers puedan elegir?',
    tags: ['plan', 'suscripcion', 'crear'],
    cta: { label: 'Ir a Plans', to: '/admin/plans' },
    answer: (
      <p>
        Admin → <strong>Plans</strong> → "Crear". Nombre, fee mensual, SMS
        incluidos, overage. Apuntás a un rate card existente. Al asignar un
        cliente al plan, ese rate card se vuelve su default.
      </p>
    ),
  },

  // =============================================================================
  // Sender pool
  // =============================================================================
  {
    id: 'senders-pool',
    category: 'senders',
    question: '¿Cómo funciona el pool de senders?',
    tags: ['pool', 'admin senders'],
    cta: { label: 'Ir a Senders', to: '/admin/senders' },
    answer: (
      <p>
        Vos como reseller registrás los senders en tu pool, los enviás a
        aprobación 10DLC del platform admin, y una vez aprobados los asignás
        a customers específicos. Un mismo sender solo puede estar asignado
        a 1 customer a la vez.
      </p>
    ),
  },
  {
    id: 'senders-submit',
    category: 'senders',
    question: '¿Cómo someto un sender a aprobación 10DLC?',
    tags: ['submit', 'aprobar'],
    answer: (
      <p>
        En Admin → Senders → detail del sender → "Submit for approval". Pasa
        a estado <code className="mono">submitted</code> y el platform admin
        lo revisa. Mientras está pending no podés asignarlo. Si es aprobado
        pasa a <code className="mono">approved</code> y queda usable.
      </p>
    ),
  },
  {
    id: 'senders-assign',
    category: 'senders',
    question: '¿Cómo asigno un sender aprobado a un customer?',
    tags: ['assign', 'asignar'],
    answer: (
      <p>
        En el detail del sender → "Assign" → elegís el customer destino. El
        sender se "marca" para ese customer y solo ese tenant puede usarlo
        en el campo From de sus envíos. Para reasignar, primero "unassign"
        del actual.
      </p>
    ),
  },

  // =============================================================================
  // Earnings / reports (reseller-only)
  // =============================================================================
  {
    id: 'earnings-read',
    category: 'reports',
    question: '¿Cómo leo el reporte de Earnings?',
    tags: ['earnings', 'ganancias', 'margen'],
    cta: { label: 'Ir a Earnings', to: '/admin/earnings' },
    answer: (
      <p>
        Earnings = sum(sell_price) - sum(buy_cost) sobre los SMS de tus
        customers en el rango. Las KPIs muestran ingresos / costo /
        margen. El gráfico mes a mes y la lista de top customers te dicen
        de dónde viene tu margen.
      </p>
    ),
  },

  // =============================================================================
  // Branding (white-label)
  // =============================================================================
  {
    id: 'branding-setup',
    category: 'branding',
    question: '¿Cómo personalizo el portal con mi marca (white-label)?',
    tags: ['white-label', 'logo', 'color'],
    cta: { label: 'Ir a Branding', to: '/admin/branding' },
    answer: (
      <p>
        Admin → <strong>Branding</strong>. Logo (PNG/SVG), color primario
        (hex), dominio custom (futuro), y branding del email transaccional.
        Los cambios aplican a TODOS tus customers — el portal lo carga
        runtime al login.
      </p>
    ),
  },
  {
    id: 'branding-email-from',
    category: 'branding',
    question: '¿Puedo cambiar el "from name" de los emails que reciben mis clientes?',
    tags: ['email', 'from', 'remitente'],
    answer: (
      <p>
        Sí — Branding → "From name" + "Reply-to". Por ejemplo, en vez de
        "EzSender", tu cliente ve "Acme Notifications &lt;noreply@getezsender.com&gt;".
        El email DOMAIN sigue siendo el de la plataforma (SPF/DKIM viven
        ahí), pero el display name es tuyo.
      </p>
    ),
  },
  {
    id: 'branding-custom-domain',
    category: 'branding',
    question: '¿Puedo usar mi propio dominio (portal.miempresa.com)?',
    tags: ['domain', 'dns', 'cname'],
    answer: (
      <p>
        Está en roadmap pero todavía no shipped. Por ahora todos los customers
        tuyos usan <code className="mono">app.getezsender.com</code>. Cuando
        la feature esté lista, configurás un CNAME → la plataforma maneja el
        TLS automático y los customers ven tu URL.
      </p>
    ),
  },

  // =============================================================================
  // Audit log
  // =============================================================================
  {
    id: 'audit-what',
    category: 'platform',
    question: '¿Qué se registra en el audit log?',
    tags: ['audit', 'log', 'auditoria'],
    cta: { label: 'Ir a Audit', to: '/admin/audit' },
    answer: (
      <p>
        Cada llamada que muta estado en <code className="mono">/v1/admin/*</code>:
        quién, cuándo, qué endpoint, body del request (con campos sensibles
        ocultos). Útil para investigar "¿quién creó este customer?",
        "¿cuándo se cambió este rate?", etc. Filtrable por actor + path +
        rango.
      </p>
    ),
  },
];
